import { api } from "./auth";

/**
 * Get all agencies
 * Agency contact info is always visible to all users
 * @param {Object} filters - Search filters
 * @param {string} [filters.location] - Filter by location
 * @param {number} [filters.skip] - Number of records to skip (pagination)
 * @param {number} [filters.limit] - Number of records to return (pagination)
 */
export async function getAgencies(filters = {}) {
  const params = new URLSearchParams();
  
  if (filters.location) {
    params.append("location", filters.location);
  }
  if (filters.skip !== undefined) {
    params.append("skip", filters.skip);
  }
  if (filters.limit !== undefined) {
    params.append("limit", filters.limit);
  }
  
  const queryString = params.toString();
  const url = queryString ? `/agencies/?${queryString}` : "/agencies/";
  
  const response = await api.get(url);
  return response.data;
}

/**
 * Get a specific agency by ID
 * @param {number} agencyId - Agency profile ID
 */
export async function getAgencyById(agencyId) {
  const response = await api.get(`/agencies/${agencyId}`);
  return response.data;
}

/**
 * Send a query to an agency
 * @param {number} agencyId - The agency profile ID to send query to
 * @param {string} queryMessage - The query message
 */
export async function sendAgencyQuery(agencyId, queryMessage) {
  const response = await api.post("/agencies/queries", {
    agency_id: agencyId,
    query_message: queryMessage,
  });
  return response.data;
}

/**
 * Get all queries sent by current partner user
 * Partners use /partners/queries/sent endpoint
 */
export async function getSentQueries() {
  const response = await api.get("/partners/queries/sent");
  return response.data;
}

/**
 * Get all queries received by agency (agency only)
 */
export async function getReceivedQueries() {
  const response = await api.get("/agencies/queries/received");
  return response.data;
}

/**
 * Get a specific query by ID
 * @param {number} queryId - The query ID
 */
export async function getQueryById(queryId) {
  const response = await api.get(`/agencies/queries/${queryId}`);
  return response.data;
}

/**
 * Respond to a query (agency only)
 * @param {number} queryId - The query ID
 * @param {string} responseMessage - The response message
 */
export async function respondToQuery(queryId, responseMessage) {
  const response = await api.patch(`/agencies/queries/${queryId}`, {
    response_message: responseMessage,
  });
  return response.data;
}

/**
 * Accept a query from partner (agency only)
 * @param {number} queryId - The query ID
 * @param {string} responseMessage - The response message
 */
export async function acceptQuery(queryId, responseMessage) {
  const response = await api.put(`/agencies/queries/${queryId}/accept`, {
    response: responseMessage,
  });
  return response.data;
}

/**
 * Reject a query from partner (agency only)
 * @param {number} queryId - The query ID
 * @param {string} responseMessage - The response message
 */
export async function rejectQuery(queryId, responseMessage) {
  const response = await api.put(`/agencies/queries/${queryId}/reject`, {
    response: responseMessage,
  });
  return response.data;
}

/**
 * Search agencies by name or description
 * @param {string} searchTerm - Search term
 */
export async function searchAgencies(searchTerm) {
  const agencies = await getAgencies();
  
  if (!searchTerm) {
    return agencies;
  }
  
  const lowerSearchTerm = searchTerm.toLowerCase();
  return agencies.filter(agency => 
    agency.agency_name.toLowerCase().includes(lowerSearchTerm) ||
    agency.description.toLowerCase().includes(lowerSearchTerm) ||
    agency.location.toLowerCase().includes(lowerSearchTerm) ||
    agency.services.toLowerCase().includes(lowerSearchTerm)
  );
}

/**
 * Search agencies by location or services (server-side)
 * @param {Object} filters - Search filters
 * @param {string} [filters.location] - Location to search
 * @param {string} [filters.services] - Services to search
 */
export async function searchAgenciesAPI(filters = {}) {
  const params = new URLSearchParams();
  
  if (filters.location) {
    params.append("location", filters.location);
  }
  if (filters.services) {
    params.append("services", filters.services);
  }
  
  const queryString = params.toString();
  const url = queryString ? `/agencies/search?${queryString}` : "/agencies/search";
  
  const response = await api.get(url);
  return response.data;
}

/**
 * Create agency profile (for newly registered agencies)
 * @param {Object} profileData - Agency profile data
 */
export async function createAgencyProfile(profileData) {
  const response = await api.post("/agencies/profile", profileData);
  return response.data;
}

/**
 * Update my agency profile (agency only)
 * @param {Object} profileData - Updated agency profile data
 */
export async function updateMyAgencyProfile(profileData) {
  const response = await api.put("/agencies/me", profileData);
  return response.data;
}

/**
 * Get my agency profile (agency only)
 */
export async function getMyAgencyProfile() {
  const response = await api.get("/agencies/me");
  return response.data;
}

/**
 * Send request to partner (agency only)
 * @param {number} partnerId - Partner ID to send request to
 * @param {string} message - Message to include
 */
export async function sendRequestToPartner(partnerId, message) {
  const encodedMessage = encodeURIComponent(message);
  const response = await api.post(`/agencies/send-request/${partnerId}?message=${encodedMessage}`);
  return response.data;
}

/**
 * Get all requests sent by my agency (agency only)
 * @param {string} [status] - Filter by status (pending, accepted, rejected)
 */
export async function getMyAgencyRequests(status = null) {
  const url = status 
    ? `/agencies/my-requests?status=${status}`
    : "/agencies/my-requests";
  
  const response = await api.get(url);
  return response.data;
}

/**
 * Get accepted partners (with contact details) - agency only
 */
export async function getAcceptedPartners() {
  const response = await api.get("/agencies/accepted-partners");
  return response.data;
}

/**
 * Get my queries (partner viewing their queries to agencies)
 */
export async function getMyQueries() {
  const response = await api.get("/agencies/queries/my-queries");
  return response.data;
}

/**
 * Respond to a query (agency only)
 * @param {number} queryId - The query ID
 * @param {string} responseMessage - The response message
 */
export async function respondToQueryAPI(queryId, responseMessage) {
  const response = await api.put(`/agencies/queries/${queryId}/respond`, 
    responseMessage, 
    {
      headers: {
        'Content-Type': 'application/json'
      }
    }
  );
  return response.data;
}

/**
 * Get agency requests received by partner (partner view)
 */
export async function getReceivedAgencyRequests() {
  const response = await api.get("/partners/agency-requests/received");
  return response.data;
}

/**
 * Accept agency request (partner only)
 * @param {number} requestId - Request ID to accept
 */
export async function acceptAgencyRequest(requestId) {
  const response = await api.put(`/partners/agency-requests/${requestId}/accept`);
  return response.data;
}

/**
 * Reject agency request (partner only)
 * @param {number} requestId - Request ID to reject
 */
export async function rejectAgencyRequest(requestId) {
  const response = await api.put(`/partners/agency-requests/${requestId}/reject`);
  return response.data;
}

/**
 * Get connected agencies (partner view - agencies they've accepted)
 */
export async function getConnectedAgencies() {
  const response = await api.get("/partners/connected-agencies");
  return response.data;
}

/**
 * Partner sends query to agency
 * @param {number} agencyId - Agency profile ID
 * @param {string} queryMessage - Query message
 */
export async function partnerSendQuery(agencyId, queryMessage) {
  const response = await api.post("/partners/send-query", {
    agency_id: agencyId,
    query_message: queryMessage,
  });
  return response.data;
}

/**
 * Get agency requests sent by my agency (agency view)
 */
export async function getAgencyRequestsSent() {
  const response = await api.get("/agencies/requests/sent");
  return response.data;
}
